import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { PublicSite } from '../../shared/api/types';
import { LocationPicker } from '../../shared/ui/LocationPicker';
import { useAsync } from '../../shared/ui/useAsync';
import { countryName } from '../../shared/geo/places';
import { LiveBadge, LivePlayer, useLiveState } from '../live/live';

/** Vista previa de la iglesia a la que pidió unirse (con lo que publica en su página): mientras espera la aprobación. */
export function ChurchPreview({ churchId }: { churchId: string }) {
  const site = useAsync(
    async (signal) => {
      const { slug } = await api.get<{ slug: string }>(`/multichurch/public/join/${churchId}`, signal);
      return api.get<PublicSite>(`/multichurch/public/${slug}/site`, signal);
    },
    [churchId],
  );
  const live = useLiveState(site.status === 'ready' ? `/multichurch/public/${site.data.slug}/live` : null);
  if (site.status !== 'ready') return null;
  const s = site.data;
  const place = [s.address, s.city, s.state, s.country ? countryName(s.country) : null].filter(Boolean).join(', ');
  const spot = s.latitude != null && s.longitude != null ? { lat: s.latitude, lng: s.longitude } : null;
  const directions = s.mapsUrl ?? (spot ? `https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lng}` : null);
  return (
    <section className="church-preview" aria-label={`Vista previa de ${s.name}`}>
      <header className="church-preview__head">
        {s.logoUrl ? <img src={s.logoUrl} alt="" className="church-preview__logo" /> : <span className="church-result__initial" aria-hidden="true">{s.name.charAt(0)}</span>}
        <div>
          <h3>{s.name}</h3>
          {s.tagline ? <p className="muted">{s.tagline}</p> : null}
          {s.pastorName ? <p className="small">Pastor: {s.pastorName}</p> : null}
        </div>
      </header>
      {live && live.status !== 'off' ? (
        <>
          <p className="small">
            <LiveBadge state={live} />
          </p>
          <LivePlayer state={live} title={s.name} />
        </>
      ) : null}
      {s.about ? <p className="pre-line church-preview__about">{s.about.length > 400 ? `${s.about.slice(0, 400).trim()}…` : s.about}</p> : null}
      {s.schedules.length ? (
        <>
          <h4 className="small">Horarios</h4>
          <ul className="wall-schedules">
            {s.schedules.map((x, i) => (
              <li key={i}>
                <strong>{x.label}</strong>
                <span className="muted">
                  {x.day} · {x.time}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {place ? <p className="small">📍 {place}</p> : null}
      {spot ? <LocationPicker readOnly value={spot} label={`Ubicación de ${s.name}`} /> : null}
      <div className="actions actions--wrap">
        {directions ? (
          <a className="btn btn--secondary" href={directions} target="_blank" rel="noopener noreferrer">
            Cómo llegar
          </a>
        ) : null}
        <Link className="btn btn--ghost" to={`/i/${s.slug}`}>
          Ver su página completa
        </Link>
      </div>
    </section>
  );
}
